import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Package, Wrench, ClipboardList, Calendar, Users,
  MapPin, ShoppingCart, ArrowRight, CheckCircle,
  ChevronRight, FileText, BarChart2,
} from 'lucide-react';

function CodeBlock({ children, language = '' }) {
  return (
    <div className="rounded-xl overflow-hidden border border-gray-700 my-4">
      {language && (
        <div className="bg-gray-800 px-4 py-1.5 text-xs text-gray-400 font-mono border-b border-gray-700">
          {language}
        </div>
      )}
      <pre className="bg-gray-900 text-green-300 text-sm font-mono p-5 overflow-x-auto leading-relaxed whitespace-pre">
        {children}
      </pre>
    </div>
  );
}

function Step({ n, text }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-600 text-white text-sm font-bold flex items-center justify-center mt-0.5">
        {n}
      </span>
      <p className="text-gray-700">{text}</p>
    </div>
  );
}

function SectionHeading({ icon: Icon, id, title, subtitle }) {
  return (
    <div id={id} className="flex items-start gap-4 mb-6 pt-4">
      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-primary-600" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

const CmmsDocsPage = () => {
  const { t } = useTranslation();

  const toc = [
    { id: 'getting-started', label: t('cmmsDocs.toc_start') },
    { id: 'assets',          label: t('cmmsDocs.toc_assets') },
    { id: 'work-orders',     label: t('cmmsDocs.toc_wo') },
    { id: 'pm-schedules',    label: t('cmmsDocs.toc_pm') },
    { id: 'inventory',       label: t('cmmsDocs.toc_inv') },
    { id: 'team',            label: t('cmmsDocs.toc_team') },
    { id: 'reports',         label: t('cmmsDocs.toc_reports') },
  ];

  return (
    <>
      <Helmet>
        <title>CMMS Guide | Documentation</title>
        <meta name="description" content="Complete guide to setting up and using the mwbTechs CMMS for asset and maintenance management." />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">

        {/* Hero */}
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                <Link to="/docs" className="hover:text-primary-600">{t('cmmsDocs.breadcrumb')}</Link>
                <ChevronRight className="w-4 h-4" />
                <span className="text-gray-900 font-medium">{t('cmmsDocs.breadcrumbPage')}</span>
              </div>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-5">
                <Package className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-3">{t('cmmsDocs.title')}</h1>
              <p className="text-lg text-gray-600 max-w-2xl">
                {t('cmmsDocs.heroSubtitle')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Body: ToC sidebar + content */}
        <div className="container-custom py-12">
          <div className="flex flex-col lg:flex-row gap-12">

            {/* Sticky ToC */}
            <aside className="lg:w-56 flex-shrink-0">
              <div className="sticky top-24 bg-gray-50 rounded-xl border border-gray-200 p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">{t('cmmsDocs.onThisPage')}</p>
                <ul className="space-y-2">
                  {toc.map(item => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className="text-sm text-gray-600 hover:text-primary-600 flex items-center gap-1.5"
                      >
                        <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" />
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 max-w-3xl space-y-16">

              {/* ── 1. Getting Started ────────────────────────────────── */}
              <section>
                <SectionHeading
                  icon={Package}
                  id="getting-started"
                  title={t('cmmsDocs.s1_title')}
                  subtitle={t('cmmsDocs.s1_subtitle')}
                />

                <div className="space-y-1 mb-5">
                  <Step n={1} text={t('cmmsDocs.s1_step1')} />
                  <Step n={2} text={t('cmmsDocs.s1_step2')} />
                  <Step n={3} text={t('cmmsDocs.s1_step3')} />
                  <Step n={4} text={t('cmmsDocs.s1_step4')} />
                </div>

                <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('cmmsDocs.s1_keyConcepts')}</p>
                  <div className="space-y-2 text-sm text-gray-600">
                    {[
                      [t('cmmsDocs.s1_branch'), t('cmmsDocs.s1_branchDesc')],
                      [t('cmmsDocs.s1_asset'),  t('cmmsDocs.s1_assetDesc')],
                      [t('cmmsDocs.s1_wo'),     t('cmmsDocs.s1_woDesc')],
                      [t('cmmsDocs.s1_pm'),     t('cmmsDocs.s1_pmDesc')],
                      [t('cmmsDocs.s1_inv'),    t('cmmsDocs.s1_invDesc')],
                    ].map(([k, v]) => (
                      <div key={k} className="flex gap-3">
                        <span className="font-semibold w-32 flex-shrink-0 text-gray-800">{k}</span>
                        <span>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* ── 2. Assets ─────────────────────────────────────────── */}
              <section>
                <SectionHeading
                  icon={Wrench}
                  id="assets"
                  title={t('cmmsDocs.s2_title')}
                  subtitle={t('cmmsDocs.s2_subtitle')}
                />

                <div className="space-y-1 mb-5">
                  <Step n={1} text={t('cmmsDocs.s2_step1')} />
                  <Step n={2} text={t('cmmsDocs.s2_step2')} />
                  <Step n={3} text={t('cmmsDocs.s2_step3')} />
                  <Step n={4} text={t('cmmsDocs.s2_step4')} />
                  <Step n={5} text={t('cmmsDocs.s2_step5')} />
                </div>

                <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 mb-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('cmmsDocs.s2_assetFields')}</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-200 text-xs">
                        <th className="pb-1.5 font-semibold w-36">{t('cmmsDocs.s2_fField')}</th>
                        <th className="pb-1.5 font-semibold">{t('cmmsDocs.s2_fDesc')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-xs text-gray-700">
                      {[
                        [t('cmmsDocs.s2_fName'),        t('cmmsDocs.s2_fNameDesc')],
                        [t('cmmsDocs.s2_fCode'),        t('cmmsDocs.s2_fCodeDesc')],
                        [t('cmmsDocs.s2_fCategory'),    t('cmmsDocs.s2_fCategoryDesc')],
                        [t('cmmsDocs.s2_fBranch'),      t('cmmsDocs.s2_fBranchDesc')],
                        [t('cmmsDocs.s2_fLocation'),    t('cmmsDocs.s2_fLocationDesc')],
                        [t('cmmsDocs.s2_fSerial'),      t('cmmsDocs.s2_fSerialDesc')],
                        [t('cmmsDocs.s2_fPurchase'),    t('cmmsDocs.s2_fPurchaseDesc')],
                        [t('cmmsDocs.s2_fNextService'), t('cmmsDocs.s2_fNextServiceDesc')],
                      ].map(([f, d]) => (
                        <tr key={f}>
                          <td className="py-1.5 pr-3 font-mono text-primary-700">{f}</td>
                          <td className="py-1.5 text-gray-600">{d}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-start gap-2 p-4 bg-blue-50 rounded-xl border border-blue-200 text-sm text-blue-800">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
                  <span>{t('cmmsDocs.s2_note')}</span>
                </div>
              </section>

              {/* ── 3. Work Orders ────────────────────────────────────── */}
              <section>
                <SectionHeading
                  icon={ClipboardList}
                  id="work-orders"
                  title={t('cmmsDocs.s3_title')}
                  subtitle={t('cmmsDocs.s3_subtitle')}
                />

                <div className="space-y-1 mb-5">
                  <Step n={1} text={t('cmmsDocs.s3_step1')} />
                  <Step n={2} text={t('cmmsDocs.s3_step2')} />
                  <Step n={3} text={t('cmmsDocs.s3_step3')} />
                  <Step n={4} text={t('cmmsDocs.s3_step4')} />
                  <Step n={5} text={t('cmmsDocs.s3_step5')} />
                  <Step n={6} text={t('cmmsDocs.s3_step6')} />
                </div>

                <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('cmmsDocs.s3_statuses')}</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {[
                      { status: t('cmmsDocs.s3_open'),       color: 'bg-blue-100 text-blue-800',     desc: t('cmmsDocs.s3_openDesc') },
                      { status: t('cmmsDocs.s3_inProgress'), color: 'bg-yellow-100 text-yellow-800', desc: t('cmmsDocs.s3_inProgressDesc') },
                      { status: t('cmmsDocs.s3_onHold'),     color: 'bg-orange-100 text-orange-800', desc: t('cmmsDocs.s3_onHoldDesc') },
                      { status: t('cmmsDocs.s3_completed'),  color: 'bg-green-100 text-green-800',   desc: t('cmmsDocs.s3_completedDesc') },
                    ].map(({ status, color, desc }) => (
                      <div key={status} className="flex items-start gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${color}`}>{status}</span>
                        <span className="text-gray-600 text-xs">{desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* ── 4. PM Schedules ───────────────────────────────────── */}
              <section>
                <SectionHeading
                  icon={Calendar}
                  id="pm-schedules"
                  title={t('cmmsDocs.s4_title')}
                  subtitle={t('cmmsDocs.s4_subtitle')}
                />

                <div className="space-y-1 mb-5">
                  <Step n={1} text={t('cmmsDocs.s4_step1')} />
                  <Step n={2} text={t('cmmsDocs.s4_step2')} />
                  <Step n={3} text={t('cmmsDocs.s4_step3')} />
                  <Step n={4} text={t('cmmsDocs.s4_step4')} />
                  <Step n={5} text={t('cmmsDocs.s4_step5')} />
                </div>

                <div className="flex items-start gap-2 p-4 bg-green-50 rounded-xl border border-green-200 text-sm text-green-800">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" />
                  <span>{t('cmmsDocs.s4_note')}</span>
                </div>
              </section>

              {/* ── 5. Inventory & Warehouses ─────────────────────────── */}
              <section>
                <SectionHeading
                  icon={ShoppingCart}
                  id="inventory"
                  title={t('cmmsDocs.s5_title')}
                  subtitle={t('cmmsDocs.s5_subtitle')}
                />

                <div className="space-y-1 mb-5">
                  <Step n={1} text={t('cmmsDocs.s5_step1')} />
                  <Step n={2} text={t('cmmsDocs.s5_step2')} />
                  <Step n={3} text={t('cmmsDocs.s5_step3')} />
                  <Step n={4} text={t('cmmsDocs.s5_step4')} />
                </div>

                <div className="flex items-start gap-2 p-4 bg-amber-50 rounded-xl border border-amber-200 text-sm text-amber-800">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                  <span>{t('cmmsDocs.s5_note')}</span>
                </div>
              </section>

              {/* ── 6. Team & Permissions ─────────────────────────────── */}
              <section>
                <SectionHeading
                  icon={Users}
                  id="team"
                  title={t('cmmsDocs.s6_title')}
                  subtitle={t('cmmsDocs.s6_subtitle')}
                />

                <div className="space-y-1 mb-5">
                  <Step n={1} text={t('cmmsDocs.s6_step1')} />
                  <Step n={2} text={t('cmmsDocs.s6_step2')} />
                  <Step n={3} text={t('cmmsDocs.s6_step3')} />
                  <Step n={4} text={t('cmmsDocs.s6_step4')} />
                </div>

                <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('cmmsDocs.s6_roles')}</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-200 text-xs">
                        <th className="pb-1.5 font-semibold w-36">{t('cmmsDocs.s6_roleCol')}</th>
                        <th className="pb-1.5 font-semibold">{t('cmmsDocs.s6_accessCol')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-xs text-gray-700">
                      {[
                        [t('cmmsDocs.s6_admin'),      t('cmmsDocs.s6_adminDesc')],
                        [t('cmmsDocs.s6_supervisor'), t('cmmsDocs.s6_supervisorDesc')],
                        [t('cmmsDocs.s6_technician'), t('cmmsDocs.s6_technicianDesc')],
                        [t('cmmsDocs.s6_viewer'),     t('cmmsDocs.s6_viewerDesc')],
                      ].map(([r, a]) => (
                        <tr key={r}>
                          <td className="py-1.5 pr-3 font-mono text-primary-700">{r}</td>
                          <td className="py-1.5 text-gray-600">{a}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* ── 7. Reports & Analytics ────────────────────────────── */}
              <section>
                <SectionHeading
                  icon={BarChart2}
                  id="reports"
                  title={t('cmmsDocs.s7_title')}
                  subtitle={t('cmmsDocs.s7_subtitle')}
                />

                <p className="text-gray-600 mb-5">
                  {t('cmmsDocs.s7_intro')}
                </p>

                <div className="space-y-4">
                  {[
                    { title: t('cmmsDocs.s7_r1'), desc: t('cmmsDocs.s7_r1Desc') },
                    { title: t('cmmsDocs.s7_r2'), desc: t('cmmsDocs.s7_r2Desc') },
                    { title: t('cmmsDocs.s7_r3'), desc: t('cmmsDocs.s7_r3Desc') },
                    { title: t('cmmsDocs.s7_r4'), desc: t('cmmsDocs.s7_r4Desc') },
                    { title: t('cmmsDocs.s7_r5'), desc: t('cmmsDocs.s7_r5Desc') },
                  ].map((r, i) => (
                    <div key={i} className="rounded-xl border border-gray-200 p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="w-4 h-4 text-primary-600" />
                        <h3 className="font-semibold text-gray-900 text-sm">{r.title}</h3>
                      </div>
                      <p className="text-sm text-gray-600">{r.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

            </main>
          </div>
        </div>

        {/* Footer CTA */}
        <section className="section-sm bg-gray-50 border-t border-gray-200">
          <div className="container-custom text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('cmmsDocs.needHelp')}</h2>
            <p className="text-gray-600 mb-6">{t('cmmsDocs.needHelpSubtitle')}</p>
            <div className="flex gap-4 justify-center">
              <Link to="/docs" className="btn btn-outline">{t('cmmsDocs.backToDocs')}</Link>
              <Link to="/contact" className="btn btn-primary">{t('cmmsDocs.contactSupport')}</Link>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default CmmsDocsPage;
